/**
 * Index - Dashboard HOME (shell)
 * Terminal de Inteligência do TikTok Shop
 */

import React, { useState, lazy, Suspense } from 'react';

import { HomeBannerCarousel } from '@/components/dashboard/HomeBannerCarousel';
import DashboardHeroCard from '@/components/dashboard/DashboardHeroCard';
import ForjaRadarCard from '@/components/dashboard/ForjaRadarCard';
import ArsenalTeaserCard from '@/components/dashboard/ArsenalTeaserCard';
import CreatorsDominanceCard from '@/components/dashboard/CreatorsDominanceCard';
import TrendingProductsCard from '@/components/dashboard/TrendingProductsCard';
import MinhaForjaCard from '@/components/dashboard/MinhaForjaCard';
import MarketplaceHomeCard from '@/components/dashboard/MarketplaceHomeCard';
import DashboardSkeleton from '@/components/skeletons/DashboardSkeleton';
import ToolsGrid from '@/components/ToolsGrid';
import { Compass, Activity, Play, Package as PackageIcon, MessageSquare } from 'lucide-react';
import { useDashboardData } from './dashboard/useDashboardData';
import { formatTimeAgo } from '@/lib/formatDate';

const AddEarningModal = lazy(() => import('@/components/minha-forja/tiktok/AddEarningModal').then(m => ({ default: m.AddEarningModal })));
const CreateAccountModal = lazy(() => import('@/components/minha-forja/tiktok/CreateAccountModal').then(m => ({ default: m.CreateAccountModal })));

export default function Index() {
  const {
    user, userName, effectiveRole,
    metrics, marketplaceMetrics, ecosystemStats,
    marketplaceStatus,
    favoriteIds, handleToggleFavorite, skipAnimation,
    explorarTools, getSettingByToolId,
    isFullyReady,
  } = useDashboardData();

  const [showAddEarningModal, setShowAddEarningModal] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);

  // Progressive rendering — no longer gate on isFullyReady; cards handle their own loading

  const communityItems = [
    marketplaceMetrics.lastNegotiationAt && { icon: MessageSquare, text: 'Nova negociação no Mercado', time: formatTimeAgo(marketplaceMetrics.lastNegotiationAt) },
    marketplaceMetrics.lastSaleAt && { icon: PackageIcon, text: 'Venda concluída no Mercado', time: formatTimeAgo(marketplaceMetrics.lastSaleAt) },
    (ecosystemStats?.viralVideosCount ?? 0) > 0 && { icon: Play, text: 'Novo vídeo adicionado ao Arsenal', time: 'Recentemente' },
    (ecosystemStats?.productsCount ?? 0) > 0 && { icon: PackageIcon, text: 'Produto em tendência detectado', time: 'Recentemente' },
  ].filter(Boolean) as { icon: React.ElementType; text: string; time: string | null }[];

  return (
    <div className="min-h-screen relative animate-in fade-in duration-300">
      <h1 className="sr-only">ForjaHub — Terminal de Inteligência do TikTok Shop</h1>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-primary/3 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[200px] md:w-[400px] h-[200px] md:h-[400px] bg-accent/3 rounded-full blur-[120px]" />
      </div>

      <section className="relative pt-3 md:pt-6 pb-4 md:pb-6 px-3 md:px-8 lg:px-12">
        <div className="mx-auto max-w-[1440px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-7 lg:gap-4">

            <div className="order-1 lg:order-none lg:col-span-8 h-full"><HomeBannerCarousel /></div>

            <div className="order-4 lg:order-none lg:col-span-4 h-full">
              <DashboardHeroCard userName={userName} creatorsCount={ecosystemStats?.creatorsCount ?? 0} productsCount={ecosystemStats?.productsCount ?? 0} viralVideosCount={ecosystemStats?.viralVideosCount ?? 0} />
            </div>

            <div className="order-5 lg:order-none lg:col-span-6 h-full"><ArsenalTeaserCard /></div>
            <div className="order-6 lg:order-none lg:col-span-6 h-full"><CreatorsDominanceCard /></div>

            <div className="order-2 lg:order-none lg:col-span-4 h-full">
              <MinhaForjaCard notesCount={metrics.notesCount} tasksCount={metrics.tasksCount} lastActivity={metrics.lastActivity} monthlyEarnings={metrics.tiktokMonthlyEarnings} onAddEarning={() => setShowAddEarningModal(true)} />
            </div>

            <div className="order-7 lg:order-none lg:col-span-4 h-full"><ForjaRadarCard /></div>
            <div className="order-3 lg:order-none lg:col-span-4 h-full"><MarketplaceHomeCard effectiveStatus={marketplaceStatus} /></div>
            <div className="order-8 lg:order-none lg:col-span-12 h-full dashboard-section-lazy"><TrendingProductsCard /></div>

            {explorarTools.length > 0 && (
              <div className="order-8 lg:order-none lg:col-span-12 dashboard-section-lazy">
                <div className="rounded-xl border border-primary/15 md:border-primary/10 bg-card/60 md:bg-card/50 backdrop-blur-sm shadow-[0_0_15px_rgba(217,170,75,0.04)] ring-1 ring-primary/[0.06] p-3 md:p-4">
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg bg-secondary/80 flex items-center justify-center">
                        <Compass className="w-3 h-3 md:w-3.5 md:h-3.5 text-muted-foreground" />
                      </div>
                      <div>
                        <h2 className="font-display text-sm md:text-base font-bold text-foreground">Ferramentas da Plataforma</h2>
                        <p className="text-[9px] md:text-[10px] text-muted-foreground hidden sm:block">{user ? 'Favoritar com ☆ para destacar' : 'Mais ferramentas do ForjaHub'}</p>
                      </div>
                    </div>
                    <span className="badge-primary text-[8px] md:text-[9px] px-1.5 py-0.5">{explorarTools.length}</span>
                  </div>
                  <ToolsGrid favoriteIds={favoriteIds} onToggleFavorite={handleToggleFavorite} showFavoriteButton={!!user} skipAnimation={skipAnimation} getSettingByToolId={getSettingByToolId} excludeIds={['minha-forja', 'marketplace', 'parceiros-forja', 'ensino', 'comunidade']} />
                </div>
              </div>
            )}

            {communityItems.length > 0 && (
              <div className="order-9 lg:order-none lg:col-span-12 dashboard-section-lazy">
                <div className="rounded-xl border border-primary/15 md:border-primary/10 bg-card/60 md:bg-card/50 backdrop-blur-sm shadow-[0_0_15px_rgba(217,170,75,0.04)] ring-1 ring-primary/[0.06] p-3 md:p-4 h-full">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 md:w-7 md:h-7 rounded-lg bg-secondary/80 flex items-center justify-center">
                      <Activity className="w-3 h-3 md:w-3.5 md:h-3.5 text-muted-foreground" />
                    </div>
                    <h2 className="font-display text-sm md:text-base font-bold text-foreground">Movimento da comunidade</h2>
                  </div>
                  <div className="space-y-1.5">
                    {communityItems.slice(0, 4).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-secondary/30 border border-border/20">
                        <item.icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs text-foreground flex-1">{item.text}</span>
                        {item.time && <span className="text-[9px] text-muted-foreground/60 flex-shrink-0">{item.time}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      {showAddEarningModal && (
        <Suspense fallback={null}>
          <AddEarningModal open={showAddEarningModal} onOpenChange={setShowAddEarningModal} onOpenCreateAccount={() => { setShowAddEarningModal(false); setShowCreateAccountModal(true); }} />
        </Suspense>
      )}
      {showCreateAccountModal && (
        <Suspense fallback={null}>
          <CreateAccountModal open={showCreateAccountModal} onOpenChange={(open) => { setShowCreateAccountModal(open); if (!open) setShowAddEarningModal(true); }} />
        </Suspense>
      )}
    </div>
  );
}
